import { type FC } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import type { AssistantRequestSummary } from "./types";
import { threadTexts } from "./texts";
import { safeHref } from "./model/safeHref";
import { initials } from "./model/people";

interface Props {
  summary: AssistantRequestSummary;
  /** Имя зрителя для букв аватара. */
  viewerName?: string | null;
}

/**
 * Первый и главный блок карточки: что просил зритель. Текст обращения,
 * тип, платёж и детали помощника для поддержки под спойлером.
 */
export const RequestSummaryCard: FC<Props> = ({ summary, viewerName }) => {
  const paymentHref = safeHref(summary.paymentHref);
  const paymentText = summary.paymentLabel
    ? `${threadTexts.payment()}: ${summary.paymentLabel}`
    : null;
  const letters = initials(viewerName);

  return (
    <Card data-block="request">
      <CardHeader
        avatar={
          <Avatar>{letters || <PersonOutlineIcon fontSize="small" />}</Avatar>
        }
        title={threadTexts.requestTitle()}
        subheader={threadTexts.createdRequest()}
        slotProps={{ title: { variant: "subtitle1", fontWeight: 600 } }}
        action={
          <Chip
            size="small"
            variant="outlined"
            label={summary.typeLabel}
            sx={{ mt: 1, mr: 1 }}
          />
        }
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <Stack spacing={2}>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: "1.0625rem",
              lineHeight: 1.6,
            }}
          >
            {summary.message}
          </Typography>
          {paymentText && (
            <Stack direction="row">
              {paymentHref ? (
                <Chip
                  variant="outlined"
                  clickable
                  component="a"
                  href={paymentHref}
                  icon={<CreditCardOutlinedIcon />}
                  label={paymentText}
                />
              ) : (
                <Chip
                  variant="outlined"
                  icon={<CreditCardOutlinedIcon />}
                  label={paymentText}
                />
              )}
            </Stack>
          )}
          {summary.details && (
            <Accordion
              variant="outlined"
              disableGutters
              sx={{
                bgcolor: "transparent",
                borderRadius: 1,
                "&::before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <NotesOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {threadTexts.details()}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {summary.details}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
